import { animated, useSpring } from '@react-spring/web';
import React, { useCallback, useRef, useState } from 'react';
import { ToggleLayer } from 'react-laag';
import { useVirtual } from 'react-virtual';
import ROPolyfill from 'resize-observer-polyfill';
import styled from 'styled-components';
import { useMediaQuery } from '../../../hooks';
import { Icon } from '../../Icon';
import { ChevronDown } from '../../Icons';
import { Box } from '../../Layout';
import { SecondaryText, Text } from '../../Typography';
import { iso2FlagEmoji } from '../utils';
import { CountryOption, Flag, FlagContainer, OptionsContainer, TriggerWrapper } from './styledComponents';
let ResizeObserver;
if (typeof window !== 'undefined') {
  ResizeObserver = window.ResizeObserver || ROPolyfill;
}
const RotationBox = animated(Box);
const StyledSearchInput = styled.input.withConfig({
  displayName: "StyledSearchInput",
  componentId: "sc-841pk7-0"
})(["{padding:10px 20px;border:none;border-bottom:solid 1px rgba(0,0,0,0.2);width:100%;font-size:inherit;line-height:inherit;font-weight:900;background-color:inherit;font-weight:400;:hover{cursor:pointer;}}"]);
export const CountrySelector = _ref => {
  let {
    countries,
    onSelect,
    iso,
    disabled
  } = _ref;
  const [searchValue, setSearchValue] = useState('');
  const options = Object.values(countries);
  const isMobile = useMediaQuery('mobile');
  const filteredCountries = options.filter(option => option.label.toLowerCase().startsWith(searchValue.toLowerCase()));
  return React.createElement(ToggleLayer, {
    disabled: disabled,
    renderLayer: _ref2 => {
      let {
        isOpen,
        layerProps,
        close
      } = _ref2;
      const handleSelect = selection => {
        onSelect === null || onSelect === void 0 || onSelect(selection);
        close();
      };
      return isOpen && !disabled && React.createElement(OptionsContainer, {
        bg: "brandLight",
        borderRadius: 1,
        maxWidth: "100%",
        overflowX: "hidden",
        boxShadow: 6,
        ref: layerProps.ref,
        style: layerProps.style
      }, React.createElement(StyledSearchInput, {
        autoFocus: true,
        type: "text",
        placeholder: "Search for a country...",
        value: searchValue,
        onChange: e => setSearchValue(e.target.value)
      }), React.createElement(OptionsVirtualized, {
        options: filteredCountries,
        onClick: handleSelect
      }));
    },
    closeOnOutsideClick: true,
    placement: {
      possibleAnchors: ['BOTTOM_CENTER', 'BOTTOM_LEFT'],
      anchor: isMobile ? 'BOTTOM_CENTER' : 'BOTTOM_LEFT',
      autoAdjust: true,
      triggerOffset: 22,
      scrollOffset: 0
    },
    ResizeObserver: ResizeObserver
  }, _ref3 => {
    let {
      toggle,
      triggerRef,
      isOpen
    } = _ref3;
    const iconSpring = useSpring({
      transform: isOpen && !disabled ? 'rotate(180deg)' : 'rotate(0deg)'
    });
    return React.createElement(TriggerWrapper, {
      marginRight: 2,
      ref: triggerRef,
      onClick: () => toggle()
    }, React.createElement(FlagContainer, {
      width: "24px",
      marginRight: 1
    }, React.createElement(Flag, {
      fontSize: 4
    }, iso2FlagEmoji(iso))), React.createElement(RotationBox, {
      style: iconSpring,
      width: "min-content"
    }, React.createElement(Icon, {
      color: "brandDark",
      fontSize: 1
    }, React.createElement(ChevronDown, null))));
  });
};
const OPTION_HEIGHT = 48;
const OptionsVirtualized = _ref4 => {
  var _window;
  let {
    options,
    onClick
  } = _ref4;
  const parentRef = useRef();
  const estimateSize = useCallback(() => OPTION_HEIGHT, []);
  const isMobile = useMediaQuery('mobile');
  const WIDTH = isMobile ? (_window = window) === null || _window === void 0 ? void 0 : _window.innerWidth : 400;
  const HEIGHT = isMobile && parentRef !== null && parentRef !== void 0 && parentRef.current ? window.innerHeight - parentRef.current.getBoundingClientRect().top : OPTION_HEIGHT * 5;
  const {
    virtualItems,
    totalSize
  } = useVirtual({
    size: options.length,
    parentRef,
    estimateSize,
    overscan: 10
  });
  return React.createElement(Box, {
    width: "".concat(WIDTH, "px"),
    height: "".concat(HEIGHT, "px"),
    overflow: "auto",
    ref: parentRef
  }, React.createElement(Box, {
    position: "relative",
    height: totalSize,
    width: "100%"
  }, virtualItems.map(virtualItem => {
    const option = options[virtualItem.index];
    return React.createElement(CountryOption, {
      key: virtualItem.index,
      size: virtualItem.size,
      start: virtualItem.start,
      innerWidth: "".concat(WIDTH - 36, "px"),
      onClick: () => onClick(option)
    }, React.createElement(FlagContainer, {
      width: "24px",
      alignItems: "center",
      marginRight: 3
    }, React.createElement(Flag, {
      fontSize: 4
    }, iso2FlagEmoji(option.id))), React.createElement(Text, {
      marginRight: 2,
      wrapped: true
    }, option.label), React.createElement(SecondaryText, {
      marginLeft: "auto"
    }, option.dialCode));
  })));
};